/**
 * GuardianNest reminders: schedules browser notifications for upcoming
 * appointments while the tab is open (or via the service worker if PWA-installed).
 * No push server needed — local scheduling only.
 */
import { supabase } from "@/lib/supabase";

const SCHEDULED = new Map(); // key -> timeoutId
const LOOK_AHEAD_MS = 12 * 60 * 60 * 1000; // 12 hours

function isNotificationSupported() {
  return typeof window !== "undefined" && "Notification" in window;
}

export function currentPermission() {
  return isNotificationSupported() ? Notification.permission : "unsupported";
}

export async function requestNotificationPermission() {
  if (!isNotificationSupported()) return "unsupported";
  if (Notification.permission === "default") {
    return await Notification.requestPermission();
  }
  return Notification.permission;
}

function showNotification(title, body, tag, url) {
  if (!isNotificationSupported() || Notification.permission !== "granted") return;
  // Prefer service worker notification (so it survives tab focus)
  if (navigator.serviceWorker?.controller) {
    navigator.serviceWorker.controller.postMessage({
      type: "SHOW_NOTIFICATION",
      title,
      body,
      tag,
      url,
    });
  } else {
    // Fallback direct Notification (needs to be in a user gesture on some browsers)
    try {
      new Notification(title, { body, tag, icon: "/brand/logo-icon.png" });
    } catch (_) {
      /* noop */
    }
  }
}

function schedule(key, whenTs, title, body, url) {
  if (SCHEDULED.has(key)) {
    clearTimeout(SCHEDULED.get(key));
    SCHEDULED.delete(key);
  }
  const delay = whenTs - Date.now();
  if (delay <= 0 || delay > LOOK_AHEAD_MS) return; // skip past or too-far-future
  const t = setTimeout(() => {
    showNotification(title, body, key, url);
    SCHEDULED.delete(key);
  }, delay);
  SCHEDULED.set(key, t);
}

/**
 * Load upcoming appointments in the next 12h and schedule notifications
 * `reminder_minutes` before each.
 */
export async function scheduleAppointmentReminders() {
  if (currentPermission() !== "granted") return;
  const nowIso = new Date().toISOString().slice(0, 10);
  const { data, error } = await supabase
    .from("appointments")
    .select("id, title, appointment_date, appointment_time, reminder_minutes, doctor_name, location, profile_name, status")
    .gte("appointment_date", nowIso)
    .eq("status", "scheduled")
    .limit(50);
  if (error || !data) return;
  data.forEach((a) => {
    if (!a.appointment_date) return;
    const timePart = a.appointment_time ? a.appointment_time.slice(0, 5) : "09:00";
    const when = new Date(`${a.appointment_date}T${timePart}:00`);
    const remindMs = Math.max(0, (a.reminder_minutes ?? 30) * 60 * 1000);
    const fireAt = when.getTime() - remindMs;
    const title = `Appointment reminder — ${a.profile_name || "family"}`;
    const time = when.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    const bodyParts = [a.title, `at ${time}`];
    if (a.doctor_name) bodyParts.push(`with ${a.doctor_name}`);
    if (a.location) bodyParts.push(a.location);
    schedule(`appt-${a.id}`, fireAt, title, bodyParts.join(" · "), "/appointments");
  });
}

/**
 * For each active medication with schedule_times[] and today's date in range,
 * fire a reminder at each scheduled clock time (once per day per time slot).
 */
export async function scheduleMedicationReminders() {
  if (currentPermission() !== "granted") return;
  const today = new Date().toISOString().slice(0, 10);
  const { data, error } = await supabase
    .from("medications")
    .select("id, name, dosage, schedule_times, profile_name, is_active, start_date, end_date")
    .eq("is_active", true)
    .limit(100);
  if (error || !data) return;
  data.forEach((m) => {
    if (!Array.isArray(m.schedule_times) || m.schedule_times.length === 0) return;
    if (m.start_date && m.start_date > today) return;
    if (m.end_date && m.end_date < today) return;
    m.schedule_times.forEach((t) => {
      const clock = String(t).slice(0, 5);
      const fireAt = new Date(`${today}T${clock}:00`).getTime();
      if (fireAt < Date.now()) return;
      schedule(
        `med-${m.id}-${clock}`,
        fireAt,
        `Time for ${m.profile_name || "family"}'s medication`,
        `${m.name} · ${m.dosage} at ${clock}`,
        "/medications"
      );
    });
  });
}

export function clearAllReminders() {
  for (const [, t] of SCHEDULED) clearTimeout(t);
  SCHEDULED.clear();
}
